import { useEffect, useState, useMemo, useCallback } from 'react'
import { useModals } from 'almanac-ui/Modal'
import { observer } from 'mobx-react-lite'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { ArrowLeft, Close } from 'blaze-ui/icons'
import { Credits } from 'blaze-ui/icons/20/Credits'
import { LinkExternal } from 'blaze-ui/icons/20/LinkExternal'
import { trackEventInHeap, HeapEventType } from 'utils/heapTracking'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { useCreditUsage } from 'hooks/useCreditUsage'
import { useShowCreditsPaywall } from 'hooks/useShowCreditsPaywall'
import { PurchaseContentCreditsModal } from 'components/Settings/TieredBilling/components/AutopilotDetails/components/PurchaseContentCreditsModal/PurchaseContentCreditsModal'
import { CountInput } from 'components/Wizards/CountInput/CountInput'
import { Accordion } from 'components/Accordion/Accordion'
import { useAccordion } from 'components/Accordion/useAccordion'
import { FeaturedMedia } from 'types'
import { ContentPlanValues } from '../../VisualStylesForExistingUsersModal'
import { ContentTypesSelector, MediaWeights } from 'screens/ContentPreferences/components/ContentTypesSelector'
import { getContentsCount } from 'screens/BrandKits/components/BrandPlan/utils'
import { useContentPreferencesFields } from 'screens/BrandKits/components/ContentPreferences/useContentPreferencesFields'
import { useCampaignsEnabled } from 'hooks/queries/brandPlans/useCampaignsEnabled'
import Styles from '../../VisualStylesForExistingUsersModal.module.scss'

interface MobileContentPlanStepProps {
  animatedImagesOptIn: boolean
  onContentPlanChange: (values: ContentPlanValues) => void
  onRegenerate: () => void
  onBack: () => void
  onClose: () => void
  isSaving: boolean
}

export const MobileContentPlanStep = observer((props: MobileContentPlanStepProps) => {
  const { animatedImagesOptIn, onContentPlanChange, onRegenerate, onBack, onClose, isSaving } = props
  const { openModal } = useModals()
  const { data: brandPlan } = useCurrentBrandPlan()
  const isCampaignsAccount = useCampaignsEnabled()
  const { usageCosts, creditsRemaining } = useCreditUsage()
  const { price } = useShowCreditsPaywall()
  const { expanded, handleHeaderClick, animationActive } = useAccordion(false, true)

  // Get initial values from brand plan
  const initialSocialPosts = useMemo(
    () => getContentsCount(brandPlan?.crosspost_contents ?? []),
    [brandPlan?.crosspost_contents],
  )
  const initialShortFormVideos = useMemo(
    () => getContentsCount(brandPlan?.video_crosspost_contents ?? []),
    [brandPlan?.video_crosspost_contents],
  )
  const initialBlogPosts = useMemo(() => getContentsCount(brandPlan?.seo_contents ?? []), [brandPlan?.seo_contents])
  const initialEmails = useMemo(() => getContentsCount(brandPlan?.email_contents ?? []), [brandPlan?.email_contents])

  // Get initial featured media and weights from brand kit content preferences
  const brandKit = brandPlan?.brand_kit
  const { preferences } = useContentPreferencesFields({ brandKit })

  const initialFeaturedMedia = preferences.featured_media
  const initialMediaWeights = useMemo(
    () => ({
      video: preferences.featured_media_video_weight,
      image: preferences.featured_media_image_weight,
      text: preferences.featured_media_text_weight,
    }),
    [
      preferences.featured_media_video_weight,
      preferences.featured_media_image_weight,
      preferences.featured_media_text_weight,
    ],
  )

  const [socialPostsCount, setSocialPostsCount] = useState(initialSocialPosts || 3)
  const [shortFormVideosCount, setShortFormVideosCount] = useState(initialShortFormVideos || 2)
  const [blogPostsCount, setBlogPostsCount] = useState(initialBlogPosts || 0)
  const [emailsCount, setEmailsCount] = useState(initialEmails || 0)
  const [featuredMedia, setFeaturedMedia] = useState<FeaturedMedia[]>(initialFeaturedMedia as FeaturedMedia[])
  const [mediaWeights, setMediaWeights] = useState<MediaWeights>(initialMediaWeights)

  // Update state when brand plan loads
  useEffect(() => {
    if (brandPlan) {
      const socialDefault = initialSocialPosts <= 2 ? 3 : initialSocialPosts
      setSocialPostsCount(socialDefault)

      const shortFormDefault = initialShortFormVideos <= 1 ? 2 : initialShortFormVideos
      setShortFormVideosCount(shortFormDefault)
      if (initialBlogPosts >= 0) setBlogPostsCount(initialBlogPosts)
      if (initialEmails >= 0) setEmailsCount(initialEmails)
    }
  }, [brandPlan, initialSocialPosts, initialShortFormVideos, initialBlogPosts, initialEmails])

  // Credit costs
  const baseContentCreditCost = usageCosts?.content_credits_cost ?? 0
  const videoCreditCost = animatedImagesOptIn ? usageCosts?.video_credits_cost ?? 0 : baseContentCreditCost
  const stylizedImageCost = usageCosts?.visual_styles_credits_cost ?? 0
  const blogEmailCost = baseContentCreditCost

  // Calculate total weekly credit cost
  const totalWeeklyCost = useMemo(() => {
    let socialCost = 0

    if (featuredMedia.includes('video')) {
      const videoPercentage = mediaWeights.video / 100
      socialCost += Math.ceil(socialPostsCount * videoPercentage * videoCreditCost)
      socialCost += Math.ceil(socialPostsCount * videoPercentage * stylizedImageCost)
    }
    if (featuredMedia.includes('image')) {
      const imagePercentage = mediaWeights.image / 100
      socialCost += Math.ceil(socialPostsCount * imagePercentage * stylizedImageCost)
    }
    if (featuredMedia.includes('text')) {
      const textPercentage = mediaWeights.text / 100
      socialCost += Math.ceil(socialPostsCount * textPercentage * baseContentCreditCost)
    }

    const shortFormCost = shortFormVideosCount * videoCreditCost + shortFormVideosCount * stylizedImageCost
    const blogCost = blogPostsCount * blogEmailCost
    const emailCost = emailsCount * blogEmailCost

    return socialCost + shortFormCost + blogCost + emailCost
  }, [
    socialPostsCount,
    shortFormVideosCount,
    blogPostsCount,
    emailsCount,
    featuredMedia,
    mediaWeights,
    videoCreditCost,
    stylizedImageCost,
    baseContentCreditCost,
    blogEmailCost,
  ])

  const handleContentTypesUpdate = useCallback((newFeaturedMedia: string[], newWeights: MediaWeights) => {
    setFeaturedMedia(newFeaturedMedia as FeaturedMedia[])
    setMediaWeights(newWeights)
  }, [])

  useEffect(() => {
    trackEventInHeap(HeapEventType.VisualStylesForExistingUsers, {
      action: 'view-content-plan-step-mobile',
    })
  }, [])

  const handleRegenerate = () => {
    trackEventInHeap(HeapEventType.VisualStylesForExistingUsers, {
      action: 'complete-content-plan-step-mobile',
    })
    onContentPlanChange({
      socialPostsCount,
      shortFormVideosCount,
      blogPostsCount,
      emailsCount,
      featuredMedia,
      mediaWeights,
    })

    if (creditsRemaining < totalWeeklyCost && price) {
      trackEventInHeap(HeapEventType.VisualStylesForExistingUsers, {
        action: 'open-add-credits-modal-mobile',
      })
      openModal(PurchaseContentCreditsModal, {
        price,
        onSuccess: () => {
          trackEventInHeap(HeapEventType.VisualStylesForExistingUsers, {
            action: 'add-credits-success-mobile',
          })
          onRegenerate()
        },
        defaultBatchCount: Math.ceil(totalWeeklyCost / 100),
      })
      return
    }

    onRegenerate()
  }

  return (
    <div className={Styles.mobileStepContainer}>
      <div className={Styles.mobileStepHeader}>
        <Button size="sm" variant="tertiary" onPress={onBack}>
          <ArrowLeft size={20} />
        </Button>
        <Button size="sm" variant="tertiary" onPress={onClose}>
          <Close size={20} />
        </Button>
      </div>

      <div className={Styles.mobileStepContent}>
        <div className={Styles.mobileContentPlanContent}>
          <div className={Styles.mobileContentPlanHeader}>
            <Heading level={1}>{isCampaignsAccount ? 'Set your campaign plan' : 'Set your content plan'}</Heading>
            <Text color="var(--dark-40)" variant="primary">
              These changes will update your plan and enable crossposting. Adjust anytime.
            </Text>
          </div>

          <div className={Styles.mobileContentPlanSections}>
            {/* Social Media Posts Section */}
            <div className={Styles.mobileContentPlanSection}>
              <div className={Styles.mobileContentPlanSectionLeft}>
                <Heading level={4}>Social Media</Heading>
                <Text variant="secondary" color="var(--dark-40)">
                  Crossposts per week
                </Text>
              </div>
              <div className={Styles.mobileContentPlanSectionRight}>
                <CountInput
                  name="socialPostsCount"
                  value={socialPostsCount}
                  onChange={setSocialPostsCount}
                  min={0}
                  max={15}
                  size="sm"
                  buttonSize="sm"
                  buttonVariant="secondary"
                  applyRedesign
                />
              </div>
            </div>

            {/* Content Type Format Selector */}
            <div className={Styles.mobileContentTypesSection}>
              <Text variant="secondary" color="var(--dark-90)">
                Choose content type format
              </Text>
              <ContentTypesSelector
                featuredMedia={featuredMedia}
                weights={mediaWeights}
                onUpdate={handleContentTypesUpdate}
                hideLabel
                creditCosts={{
                  video: `${videoCreditCost} credits per ${animatedImagesOptIn ? 'AI video' : 'video'}`,
                  image: `${stylizedImageCost} credits per image`,
                  text: `${baseContentCreditCost} credits per text`,
                }}
              />
            </div>

            {/* Short-form Videos Section */}
            <div className={Styles.mobileContentPlanSection}>
              <div className={Styles.mobileContentPlanSectionLeft}>
                <Heading level={4}>Short-form Videos</Heading>
                <div className={Styles.creditCostItem}>
                  <Credits size={12} color="var(--dark-40)" />
                  <Text variant="metadata" color="var(--dark-40)">
                    {videoCreditCost} credits per video
                  </Text>
                </div>
              </div>
              <div className={Styles.mobileContentPlanSectionRight}>
                <CountInput
                  name="shortFormVideosCount"
                  value={shortFormVideosCount}
                  onChange={setShortFormVideosCount}
                  min={0}
                  max={15}
                  size="sm"
                  buttonSize="sm"
                  buttonVariant="secondary"
                  applyRedesign
                />
              </div>
            </div>

            {/* Blog Posts Section */}
            <div className={Styles.mobileContentPlanSection}>
              <div className={Styles.mobileContentPlanSectionLeft}>
                <div className={Styles.mobileContentPlanSectionLeftHeader}>
                  <Heading level={4}>Blog Posts</Heading>
                  <Text variant="secondary" color="var(--dark-90)">
                    / per week
                  </Text>
                </div>

                <div className={Styles.creditCostItem}>
                  <Credits size={12} color="var(--dark-40)" />
                  <Text variant="metadata" color="var(--dark-40)">
                    {blogEmailCost} credits per blog
                  </Text>
                </div>
              </div>
              <div className={Styles.mobileContentPlanSectionRight}>
                <CountInput
                  name="blogPostsCount"
                  value={blogPostsCount}
                  onChange={setBlogPostsCount}
                  min={0}
                  max={15}
                  size="sm"
                  buttonSize="sm"
                  buttonVariant="secondary"
                  applyRedesign
                />
              </div>
            </div>

            {/* Emails Section */}
            <div className={Styles.mobileContentPlanSection}>
              <div className={Styles.mobileContentPlanSectionLeft}>
                <div className={Styles.mobileContentPlanSectionLeftHeader}>
                  <Heading level={4}>Emails</Heading>
                  <Text variant="secondary" color="var(--dark-90)">
                    / per week
                  </Text>
                </div>
                <div className={Styles.creditCostItem}>
                  <Credits size={12} color="var(--dark-40)" />
                  <Text variant="metadata" color="var(--dark-40)">
                    {blogEmailCost} credits per email
                  </Text>
                </div>
              </div>
              <div className={Styles.mobileContentPlanSectionRight}>
                <CountInput
                  name="emailsCount"
                  value={emailsCount}
                  onChange={setEmailsCount}
                  min={0}
                  max={15}
                  size="sm"
                  buttonSize="sm"
                  buttonVariant="secondary"
                  applyRedesign
                />
              </div>
            </div>

            {/* FAQ Accordion */}
            <div className={Styles.mobileContentPlanFaq}>
              <Accordion
                expanded={expanded}
                onHeaderClick={handleHeaderClick}
                animationActive={animationActive}
                allowCollapsingAll
              >
                <Accordion.Pane title="What uses credits?">
                  <div className={Styles.faqContent}>
                    <Text variant="secondary">{baseContentCreditCost} credits per content</Text>
                    <Text variant="secondary">{stylizedImageCost} credits per styled image</Text>
                    <Text variant="secondary">{videoCreditCost} credits per AI video</Text>
                  </div>
                </Accordion.Pane>
                <Accordion.Pane title="How much do credits cost?">
                  <Text variant="secondary">100 credits = $10</Text>
                </Accordion.Pane>
              </Accordion>
              <a
                href="https://help.blaze.ai/credits"
                target="_blank"
                rel="noopener noreferrer"
                className={Styles.mobileLearnMoreLink}
              >
                <Text variant="secondary" fontWeight="500" color="var(--dark-80)">
                  Learn More About Credits
                </Text>
                <LinkExternal size={16} color="var(--dark-80)" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className={Styles.mobileContentPlanFooterContainer}>
        <div className={Styles.mobileContentPlanFooter}>
          <Text variant="metadata" color="var(--dark-40)">
            This content frequency will cost {totalWeeklyCost} credits per week
          </Text>
          <Button
            variant="primary"
            size="lg"
            onPress={handleRegenerate}
            isDisabled={isSaving}
            className={Styles.mobileRegenerateButton}
          >
            <span>Regenerate Content</span>
            <Credits size={16} color="var(--light-100)" />
            <span>{totalWeeklyCost}</span>
          </Button>
        </div>
      </div>
    </div>
  )
})
